import { DEFAULT_MAP_SOURCE } from '@env';

import { CommonModule } from '@angular/common';
import { NgModule, Provider, ProviderToken, Type } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { FsMapGeometryServiceProvider, FsMapMarkersServiceProvider } from '@lib/geo/core';
import {
  EsriMapMarkersService,
  EsriMarkerAdapter,
  EsriMarkerBadgeService,
  EsriMarkerEventService,
  EsriMarkerFactoryService,
  EsriMarkerGeometryService,
  EsriMarkerQueryService,
  EsriMarkerStyleService,
  EsriModule,
} from '@lib/geo/esri';
import {
  GoogleMapMarkersService,
  GoogleMapModule,
  GoogleMarkerAdapter,
  GoogleMarkerBadgeService,
  GoogleMarkerEventService,
  GoogleMarkerFactoryService,
  GoogleMarkerGeometryService,
  GoogleMarkerQueryService,
  GoogleMarkerStyleService,
} from '@lib/geo/google/map';
import { Maps, SnGeometryToStringPipe } from '@lib/geo/helpers';
import {
  EsriMapGeometryProvider,
  GoogleMapGeometryProvider,
  MapGeometryFacadeService,
} from '@lib/map-geometry';
import { TranslationModule } from '@lib/translation';
import { SnButtonModule } from '@lib/ui/button';
import { SnIconModule } from '@lib/ui/icon';

import {
  FsDeckNotificationComponent,
  FsMapComponent,
  FsPopupComponent,
  MapControlComponent,
} from './components';
import { MapMarkersFacadeService } from './facades/map-markers-facade.service';

const MAP_SOURCE: string = DEFAULT_MAP_SOURCE;

function createConditionalProviders<
  TBase,
  TGoogleService extends TBase,
  TEsriService extends TBase,
>(
  providesFor: ProviderToken<TBase>,
  googleToken: Type<TGoogleService>,
  esriToken: Type<TEsriService>,
): Provider[] {
  const implementationToken = MAP_SOURCE === Maps.ESRI ? esriToken : googleToken;

  return [
    implementationToken,
    {
      provide: providesFor,
      useExisting: implementationToken,
    },
  ];
}

const geometryProviders: Provider[] = [
  MapGeometryFacadeService,
  ...createConditionalProviders(
    FsMapGeometryServiceProvider,
    GoogleMapGeometryProvider,
    EsriMapGeometryProvider,
  ),
];

const markerProviders: Provider[] = [
  // Conditionally provide Google marker adapter and services only when Google is the map source
  ...(MAP_SOURCE === Maps.GOOGLE
    ? [
        GoogleMarkerFactoryService,
        GoogleMarkerAdapter,
        GoogleMarkerBadgeService,
        GoogleMarkerEventService,
        GoogleMarkerGeometryService,
        GoogleMarkerQueryService,
        GoogleMarkerStyleService,
      ]
    : []),
  // Conditionally provide Esri marker adapter and services only when Esri is the map source
  ...(MAP_SOURCE === Maps.ESRI
    ? [
        EsriMarkerFactoryService,
        EsriMarkerAdapter,
        EsriMarkerBadgeService,
        EsriMarkerEventService,
        EsriMarkerGeometryService,
        EsriMarkerQueryService,
        EsriMarkerStyleService,
      ]
    : []),
  // Conditionally provide the correct marker service based on map source
  ...createConditionalProviders(
    FsMapMarkersServiceProvider,
    GoogleMapMarkersService,
    EsriMapMarkersService,
  ),
  {
    provide: 'MAP_MARKERS_PROVIDER',
    useExisting: FsMapMarkersServiceProvider,
  },
  MapMarkersFacadeService,
];

@NgModule({
  imports: [
    GoogleMapModule,
    EsriModule,
    CommonModule,
    SnIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    TranslationModule,
    SnButtonModule,
    TranslationModule,
  ],
  exports: [
    SnGeometryToStringPipe,
    MapControlComponent,
    FsMapComponent,
    FsDeckNotificationComponent,
  ],
  declarations: [
    SnGeometryToStringPipe,
    MapControlComponent,
    FsDeckNotificationComponent,
    FsMapComponent,
    FsPopupComponent,
  ],
  providers: [...geometryProviders, ...markerProviders],
})
export class SnMapModule {}
